// 頁型模板③：複雜表單（28 筆 route）。
//
// 原系統對應：整頁式的 `form-horizontal`，欄位分區塊排列，底部一組「儲存／取消」。
//   - 欄位三段結構 `<div class="row form-group"><label class="col-sm-3 control-label">…`
//     （layout/navbar.jsp:127-142 是同一個樣式的最小範例）→ <FormGrid> + <FormGridField>
//   - 存檔前確認 `sysConfirm("確定要儲存嗎？")`（js-my/instruction/courseDepartmentCode.js:48）
//     → useDialogs().confirm
//   - 欄位錯誤：原系統把訊息串成一段 HTML 丟給 showAlertDialog
//     （例 js-my/clazzList.js:2098-2116 `errMsg += '請選擇醫院<br>'`），
//     **不會標在欄位上**。雛形改成標在欄位旁（FormGridField error）＋保留整體 alert，
//     這是刻意補上原系統缺陷，模組 Markdown 的「已知原系統缺陷」欄要記。
//
// 與 CrudListPage 的差別：資料是「單一筆」而不是列表，沒有表格也沒有 modal，
// 而且多了 view-only 狀態。共用只到第二層元件為止。
//
// 支援的 scenario（§5.2）：default / validation-error / confirmation / success / view-only
// 共通資料狀態：loading（ProcessingOverlay）／ error ／ unauthorized（ErrorFrame）
//
// 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】

import { type ReactElement, useEffect, useRef, useState } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { FormMessage } from '@/components/base/FormMessage';
import {
  ContentPanel,
  FormGrid,
  FormGridField,
  ProcessingOverlay,
  useDialogs,
} from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { LegacyAsideBlocks } from '@/mocks/legacy-aside-notes';
import { t } from '@/messages/zh-TW';
import {
  columnsFor,
  initialValues,
  legacyContractFieldsFor,
  resolveAllLegacyMessages,
  resolveLegacyMessageForAction,
  toPayload,
  useDataSource,
  useList,
  useMessageKeyOverride,
  useScenario,
} from '@/mocks';
import { LegacyContractFields } from './LegacyContractFields';
import { pageBehavior } from './page-behaviors';
import {
  DialogHost,
  ExtraValidationMessages,
  extraMessagesOf,
  FieldControl,
  fieldErrorsOf,
  type PageColumn,
  TemplateError,
  toFormValues,
  toUiKey,
} from './shared';
import type { PageTemplateProps } from './types';

/**
 * 分兩個區塊：前半「基本資料」、後半「其他資料」。
 *
 * ponytail: 雛形的欄位來源是 fixtures 的四種 shape，沒有「哪些欄位屬於哪一區」的資訊，
 * 對半切是最誠實的做法 —— 它明確是佔位，不會被誤讀成已經比對過原系統的分區。
 * 升級路徑：Gate 4 各模組欄位定案後，由該模組的第三層元件提供真正的分區。
 */
function splitSections(columns: readonly PageColumn[]): readonly [PageColumn[], PageColumn[]] {
  const middle = Math.ceil(columns.length / 2);
  return [columns.slice(0, middle), columns.slice(middle)];
}

export function ComplexFormPage({ route }: PageTemplateProps): ReactElement {
  return (
    <DialogHost route={route}>
      <ComplexFormBody route={route} />
    </DialogHost>
  );
}

function ComplexFormBody({ route }: PageTemplateProps) {
  const pageId = route.pageId;
  // 批次 13（B3-3）：complex-form 頁型的整頁自訂 body（試卷派送：查詢表單＋listTable＋分派
  // modal）；未登錄頁零影響（editModal 同款 opt-in）。放在 hooks 之前——本頁不需要任何
  // 模板狀態，自訂 body 自行取資料。
  const complexBehavior = pageBehavior(pageId);
  if (complexBehavior?.complexBody !== undefined) {
    return complexBehavior.complexBody({ pageId });
  }
  const scenario = useScenario();
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  // FC-5 R4（C1-c‴）：見 CrudListPage.tsx 同名變數的說明。
  const messageKeyOverride = useMessageKeyOverride();
  const columns = columnsFor(pageId);
  const [mainSection, moreSection] = splitSections(columns);
  // FC-3：原系統該頁主體區的實測欄位（含 name／type／required／hidden／static options）。
  // 有這份資料時就用它取代 fixtures 的通用欄位——通用欄位沒有 legacy 的 name，
  // 在功能契約的 fields 維度上是「畫了但量不到」（設計 A.1）。
  const contractFields = legacyContractFieldsFor(pageId);

  // 表單頁在原系統是「帶著一筆資料的編輯畫面」。雛形沒有 URL 參數可以帶 id，
  // 所以取列表第一筆當作被編輯的那一筆 —— 一樣是走 @/mocks 的 hook，沒有直接碰 fixture。
  const { data, loading, error, reload } = useList(pageId, { page: 1, pageSize: 1 });
  const record = data?.rows[0] ?? null;

  const [values, setValues] = useState<Record<string, string>>(() =>
    initialValues(contractFields ?? [], pageId),
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // FC-5 R3：這頁 validation 訊息數超過可用欄位數時，多出來的訊息（多槽位顯示）。
  const [extraErrors, setExtraErrors] = useState<readonly string[]>([]);
  const [saved, setSaved] = useState(scenario === 'success');
  const [busy, setBusy] = useState(false);

  const viewOnly = scenario === 'view-only';

  // 資料回來後填進表單。record 換了（reload 之後）就重填。
  // columns 由 pageId 決定，pageId 在一次掛載內不會變，不放進相依陣列。
  //
  // FC-3：走契約欄位時**不預填**——原系統的值一個都沒有搬過來（值的政策見
  // LegacyContractFields 檔頭），而 fixtures 的假資料是照 fixture 欄位 key 產的，
  // 塞進 legacy 的 name 只會產生對不上的假值。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 見上方說明
  useEffect(() => {
    if (record !== null && contractFields === undefined) {
      setValues(toFormValues(columns, record));
    }
  }, [record]);

  /** 真正送出。confirm 由 handleSave 負責，讓 scenario 預設集可以略過確認直接看驗證結果。 */
  async function persist(next: Record<string, string>) {
    setFieldErrors({});
    setExtraErrors([]);
    setBusy(true);
    try {
      // criteria C5-h：每一個帶 name 的欄位，值真的進 update 的 payload。
      await dataSource.update(
        pageId,
        String(record?.id ?? ''),
        contractFields === undefined ? next : toPayload(next, contractFields, pageId),
      );
      setSaved(true);
      reload();
      // FC-5 R2（C1-c′）：這頁若有 legacy 的 success 訊息就顯示它，否則退回雛形通用文案。
      await dialogs.info(resolveLegacyMessageForAction(pageId, 'success', 'save')?.text ?? t('state.saveSuccess'));
    } catch (cause: unknown) {
      const fields = fieldErrorsOf(cause);
      setFieldErrors(fields);
      // FC-5 R3：`mock-data-source.ts` 的 gate() 依 `columnsFor()`（fixture 通用欄位模型）
      // 切分欄位錯誤，但這頁若走 `LegacyContractFields`（contractFields !== undefined，
      // 真正的 legacy 欄位），fixture 欄位鍵對不到任何畫出來的輸入框，逐欄位訊息會全部
      // 顯示不出來——不是只有「欄位數不夠裝」的溢出部分。這種情況改用完整清單
      // （resolveAllLegacyMessages，不是只讀 cause.extraMessages 的溢出子集），
      // 保證這頁的驗證訊息一定有地方顯示；有逐欄位顯示可用時仍用 extraMessagesOf() 的
      // 溢出子集，避免整份清單跟欄位訊息重複太多。
      setExtraErrors(
        contractFields === undefined
          ? extraMessagesOf(cause)
          : resolveAllLegacyMessages(pageId, 'validation').map((item) => item.text),
      );
      if (Object.keys(fields).length === 0) {
        await dialogs.alert(
          resolveLegacyMessageForAction(pageId, 'failure', 'save', messageKeyOverride)?.text ??
            t('error.generic'),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    const confirmed = await dialogs.confirm(
      resolveLegacyMessageForAction(pageId, 'confirm', 'save', messageKeyOverride)?.text ??
        t('confirm.save'),
    );
    if (!confirmed) {
      return;
    }
    await persist(values);
  }

  function handleCancel() {
    setFieldErrors({});
    setExtraErrors([]);
    setValues(
      contractFields === undefined ? toFormValues(columns, record) : initialValues(contractFields, pageId),
    );
  }

  // ── scenario 預設集：confirmation 停在確認框、validation-error 停在欄位錯誤 ──
  const autoRan = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: 只在資料首次就緒時跑一次。
  useEffect(() => {
    if (autoRan.current || loading || error !== null || record === null) {
      return;
    }
    if (scenario === 'confirmation') {
      autoRan.current = true;
      void handleSave();
    } else if (scenario === 'validation-error') {
      autoRan.current = true;
      // 刻意略過確認框直接送出：要呈現的是「欄位錯誤」這個狀態，不是確認框。
      void persist(contractFields === undefined ? toFormValues(columns, record) : values);
    }
  }, [scenario, loading, error, record]);

  function renderSection(sectionId: string, title: string, sectionColumns: readonly PageColumn[]) {
    return (
      <section data-ui-id={`${pageId}-section-${sectionId}`} className="mb-4">
        <h3
          data-ui-id={`${pageId}-section-${sectionId}-heading`}
          className="mb-2 font-bold text-sm"
        >
          {title}
        </h3>
        <FormGrid uiId={`${pageId}-section-${sectionId}-grid`} columns={2}>
          {sectionColumns.map((column) => {
            const fieldKey = toUiKey(column.key);
            const message = fieldErrors[column.key];
            return (
              <FormGridField
                key={column.key}
                uiId={`${pageId}-section-${sectionId}-field-${fieldKey}`}
                label={column.label}
                span={column.kind === 'text' ? 2 : 1}
                {...(message === undefined ? {} : { error: message })}
              >
                <FieldControl
                  uiId={`${pageId}-section-${sectionId}-input-${fieldKey}`}
                  column={column}
                  value={values[column.key] ?? ''}
                  readOnly={viewOnly}
                  invalid={message !== undefined}
                  onChange={(next) => {
                    setValues((current) => ({ ...current, [column.key]: next }));
                  }}
                />
              </FormGridField>
            );
          })}
        </FormGrid>
      </section>
    );
  }

  return (
    // 模板只負責內容區：全站外框（ApplicationShell／Navbar／SidebarMenu／Breadcrumb）
    // 由路由層的 AppLayout 提供，模板再包一層會變成殼中殼。
    // 這個 <div> 是整頁的根節點，data-ui-id 就是 §10.1 說的 pageId 本身。
    <div data-ui-id={pageId}>
      {/*
        FC-4：原系統該頁的對話框（trigger tier A–D）。
        trigger 鈕帶 `data-modal-trigger`（FUNCTIONAL-CONTRACT.md D-3），
        對話框本身條件渲染 —— 唯讀 GET 抽不到是**正確結果**，React 側證據一律走
        `tests/react/extract-modals.spec.ts` 的互動擷取。
        三級（global／shared／unique）都由這裡畫：本專案沒有 AppLayout 這一層，
        而三級的內容全部來自同一份 legacy 實測資料，分開手抄只會多一次抄錯的機會。
      */}
      <LegacyContractModals pageId={pageId} />
      <ContentPanel
        uiId={`${pageId}-panel`}
        title={route.title}
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        {error !== null ? (
          <TemplateError
            uiId={`${pageId}-error`}
            error={error}
            onRetry={reload}
            pageId={pageId}
            {...(messageKeyOverride === undefined ? {} : { messageKeyOverride })}
          />
        ) : (
          <>
            {saved ? (
              <FormMessage uiId={`${pageId}-message-success`} tone="success">
                {resolveLegacyMessageForAction(pageId, 'success', 'save')?.text ?? t('state.saveSuccess')}
              </FormMessage>
            ) : null}

            {contractFields === undefined ? (
              <>
                {renderSection('main', t('form.sectionMain'), mainSection)}
                {renderSection('more', t('form.sectionMore'), moreSection)}
              </>
            ) : (
              // FC-3：原系統該頁真正的欄位（帶 name），取代 fixtures 的通用欄位。
              // 只畫一區：分區資訊來自 fixtures 的四種 shape，對契約欄位沒有意義，
              // 硬對半切會產生一個假的「其他資料」標題（設計 :48-51 的 ponytail 註解
              // 已經說明那個對半切只是佔位）。
              <section data-ui-id={`${pageId}-section-main`} className="mb-4">
                <h3
                  data-ui-id={`${pageId}-section-main-heading`}
                  className="mb-2 font-bold text-sm"
                >
                  {t('form.sectionMain')}
                </h3>
                <FormGrid uiId={`${pageId}-section-main-grid`} columns={2}>
                  <LegacyContractFields
                    pageId={pageId}
                    fields={contractFields}
                    values={values}
                    readOnly={viewOnly}
                    disabled={busy || loading}
                    onChange={(name, next) => {
                      setValues((current) => ({ ...current, [name]: next }));
                    }}
                  />
                </FormGrid>
              </section>
            )}

            {/* FC-5 R3：這頁 validation 訊息數超過可用欄位數、或這頁走
                LegacyContractFields（沒有逐欄位錯誤顯示）時，補在這裡完整顯示。 */}
            <ExtraValidationMessages uiId={`${pageId}-form`} messages={extraErrors} />

            {/* view-only：欄位全部唯讀，而且**整組按鈕不存在**。
                原系統就是這個行為 —— 靠 Shiro 權限標籤直接不 render 儲存鈕
                （例 courseDepartmentCode.jsp:164-166 `<shiro:hasPermission>` 包住 #save），
                不是畫一顆 disabled 的鈕，也不是另外顯示一行「唯讀」說明。 */}
            {viewOnly ? null : (
              <div data-ui-id={`${pageId}-actions`} className="flex flex-wrap gap-2">
                <Button
                  uiId={`${pageId}-actions-save`}
                  variant="primary"
                  size="sm"
                  type="button"
                  disabled={busy || loading}
                  // D-1：這顆真的會打 `dataSource.update(pageId, id, values)`（persist :99）。
                  // 同一排的「取消」只還原 state，不標（criteria C5-j）。
                  dataEffect={`update:${pageId}`}
                  onClick={() => {
                    void handleSave();
                  }}
                >
                  {t('action.save')}
                </Button>
                <Button
                  uiId={`${pageId}-actions-cancel`}
                  variant="default"
                  size="sm"
                  type="button"
                  disabled={busy || loading}
                  onClick={handleCancel}
                >
                  {t('action.cancel')}
                </Button>
              </div>
            )}
            {/* 批次 43（#41 GNL-005-e，裁決③）：legacy 說明區（form sibling；formGenerator.jsp:162-182
                等）。behavior.asideNote（含互動件）優先，否則泛用文字地圖；未命中＝零影響。 */}
            {complexBehavior?.asideNote !== undefined ? complexBehavior.asideNote({ pageId }) : <LegacyAsideBlocks pageId={pageId} />}
          </>
        )}
      </ContentPanel>
      {/* 表單頁沒有表格可以蓋「處理中...」，改用原系統 showProcessing() 的整頁遮罩
          （js-my/common.js:443）。 */}
      <ProcessingOverlay uiId={`${pageId}-processing`} open={loading} />
    </div>
  );
}
